import { Core, formValidation, notEmpty, regexp } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { Alert, Button, Col, Form, FormControl, FormLabel, FormSelect, Modal, Row } from "react-bootstrap";
import { IActionResult, IArboristTreeNode, IFooter, IHeader, ILanguage, IPageHierarchy } from "../../../models/Interfaces";
import { ContentService } from "../../../services/ContentService";
import { AddUpdatePageCommand } from "../../../models/content/AddUpdatePageCommand";
import HSelect from "../../components/common/HSelect";

export interface IAddPageDialogueRef {
  savePage: () => Promise<IActionResult | null>;
  getPage: () => Promise<AddUpdatePageCommand | null>;
}

interface IAddPageDialogue {
  SelectedParent?: IArboristTreeNode | null;
  DefaultLanguage?: string | null;
  DisallowedAlias?: string | null;
  // onPageAdded?: (result: IActionResult) => void;
}

const AddPageDialogue = forwardRef((props: IAddPageDialogue, ref) => {
  const [headers, setHeaders] = useState<IHeader[]>([]);
  const [footers, setFooters] = useState<IFooter[]>([]);
  const [languages, setLanguages] = useState<ILanguage[]>([]);
  const [pagedetail, setPageDetail] = useState<AddUpdatePageCommand>(new AddUpdatePageCommand());
  const [pages, setPages] = useState<IPageHierarchy[]>([]);
  const [langSelected, setLangSelected] = useState<boolean>(false);

  const contentSvcRef = useRef<ContentService>();
  const fvRef = useRef<Core | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  useImperativeHandle(ref, () => ({
    savePage,
    getPage
  }));

  const getLanguages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const res = await contentSvcRef.current.getSiteLanguages();

      if (res?.length) {
        setLanguages(res);
      }
    }
  };

  const getHeaders = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const res = await contentSvcRef.current.getSiteHeaders();
      if (res?.length) {
        setHeaders(res);
      }
    }
  };

  const getFooters = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const res = await contentSvcRef.current.getSiteFooters();

      if (res?.length) {
        setFooters(res);
      }
    }
  };

  const getPagesForLanguage = async (lang: string | null): Promise<void> => {
    if (lang) {
      const res = await contentSvcRef.current?.getPagesForLanguage(lang);
      if (res?.length) {
        setPages(res);
        setLangSelected(true);
      }
    } else {
      setPages([]);
    }
  };

  const normalizeUrl = (url: string | null | undefined): string => {
    if (!url) {
      return "";
    }

    const trimmed = url.trim();
    if (trimmed === "" || trimmed === "/") {
      return trimmed === "/" ? "/" : "";
    }

    return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
  };

  const resolveParentFromUrl = (parentUrl: string | null | undefined): void => {
    const normalizedParentUrl = normalizeUrl(parentUrl);

    if (!normalizedParentUrl || pages.length === 0) {
      return;
    }

    const selectedParent = pages.find((page) => normalizeUrl(page.Url) === normalizedParentUrl);

    if (selectedParent) {
      setPageDetail((prev) => ({
        ...prev,
        ParentPageId: selectedParent.Id,
        ParentUrl: selectedParent.Url
      }));
    }
  };

  const getSelectedParentOptionId = (): number | "" => {
    if (pages.length === 0) {
      return "";
    }

    const selectedParent = pages.find((page) =>
      (pagedetail.ParentPageId !== null && pagedetail.ParentPageId !== undefined && Number(page.Id) === Number(pagedetail.ParentPageId)) ||
      normalizeUrl(page.Url) === normalizeUrl(pagedetail.ParentUrl)
    );

    return selectedParent?.Id ?? "";
  };

  const pageUrl = (): string => {
    let slug = (pagedetail.Slug || "").trim();
    let parentUrl = (pagedetail.ParentUrl || "").trim();
  
    if (parentUrl && parentUrl !== "/") {
      return `https://<site>${parentUrl}/${slug}`;
    } else {
      return `https://<site>/${slug}`;
    }
  };
  

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    if (name) {
      const isTitleField = name.toLowerCase() === "title";
      const nextValue = isTitleField ? value : value.trim();

      if (isTitleField) {
        fvRef.current?.revalidateField("Slug");
      }

      setPageDetail((prev) => {
        const normalizedTitle = value.trim();
        const slugalias = isTitleField
          ? normalizedTitle.toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/-+/g, "-").replace(/^[_-]+|[_-]+$/g, "")
          : "";

        const nextPageDetail = {
          ...prev,
          [name]: nextValue,
          ...(isTitleField && {
            Slug: slugalias,
            Alias: slugalias
          })
        };

        return {
          ...nextPageDetail,
          Url: (() => {
            const slug = (nextPageDetail.Slug || "").trim();
            const parentUrl = (nextPageDetail.ParentUrl || "").trim();
            return (parentUrl && parentUrl !== "/")
              ? `${parentUrl}/${slug}`
              : `/${slug}`;
          })()
        };
      });
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = event.target;

    if (name) {
      setPageDetail((prev) => ({
        ...prev,
        [name]: checked
      }));
    }
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = event.target;
    const pval: string | null = value === "" ? null : value;
    setPageDetail((prev) => {
      const nextPageDetail: AddUpdatePageCommand = {
        ...prev,
        [name]: pval
      };

      if (name === "ParentPageId") {
        if (pval === null) {
          nextPageDetail.ParentPageId = null;
          nextPageDetail.ParentUrl = "";
        } else {
          const parentPageId = !isNaN(Number(pval)) ? Number(pval) : null;
          const selectedParent = pages.find((page) => Number(page.Id) === parentPageId);
          if (selectedParent) {
            nextPageDetail.ParentPageId = selectedParent.Id;
            nextPageDetail.ParentUrl = selectedParent.Url;
          }
          // if not found, keep prev values — don't wipe
        }
      }

      return nextPageDetail;
    });

    if (name === "Language") {
      getPagesForLanguage(pval);
      if (pval && pval !== "") {
        setLangSelected(true);
      } else {
        setLangSelected(false);
      }
    }
  };

  const savePage = async (): Promise<IActionResult | null> => {
    if (pagedetail && fvRef.current && contentSvcRef.current) {
      const res = await fvRef.current.validate();

      if (res === "Valid") {
        pagedetail.Url = pageUrl().replace("https://<site>", ""); // double check for Url - important bit
        const saveres = await contentSvcRef.current.addUpdatePage(pagedetail);
        return saveres;
      }
    }
    return null;
  };

  const getPage = async () : Promise<AddUpdatePageCommand | null> => {
    if (fvRef.current) {
      const res = await fvRef.current.validate();

      if (res === "Valid") {
        return pagedetail;
      }
    }
    return null;
  };

  const createValidator = (): void => {
    const formelem = document.getElementById("AddPageForm") as HTMLFormElement;

    if (formelem) {
      fvRef.current = createFormValidation(formelem, {
        fields: {
          Title: {
            validators: {
              notEmpty: { message: "title is required" }
            }
          },
          Alias: {
            validators: {
              notEmpty: { message: "alias is required" },
              regexp: {
                regexp: /^(?![_-])[a-zA-Z0-9 _-]+(?<![_-])$/,
                message: "alias contains invalid characters"
              },
              callback: {
                callback: (field: { value?: string }) => {
                  const aliasValue = (field?.value ?? "").trim().toLowerCase();
                  const disallowedAlias = (props.DisallowedAlias ?? "").trim().toLowerCase();

                  if (!aliasValue || !disallowedAlias) {
                    return {
                      valid: true
                    };
                  }

                  return {
                    valid: aliasValue !== disallowedAlias,
                    message: "alias cannot be the same as the source page alias"
                  };
                }
              }
            }
          },
          Slug: {
            validators: {
              notEmpty: { message: "url slug is required" },
              regexp: {
                regexp: /^(?![_-])[a-zA-Z0-9_-]+(?<![_-])$/,
                message: "The slug must only contain letters, numbers, hyphens (-), or underscores (_), and cannot start or end with a hyphen or underscore."
              }
            }
          },
          Language: {
            validators: {
              callback: {
                callback: (field: any) => {
                  const result = {
                    valid: false,
                    message: "please select a language"
                  };

                  if (field?.value && field.value !== "") {
                    result.valid = true;
                    result.message = "";
                  }
                  return result;
                }
              }
            }
          }
        },
        plugins: {
          trigger: new Trigger(),
          bootstrap5: new Bootstrap5(),
          submitButton: new SubmitButton()
        }
      })
    }
  };

  useEffect(() => {
    const pageid = props.SelectedParent?.id
      ? Number(props.SelectedParent.id)
      : 0;
    setPageDetail((prev) => ({
      ...prev,
      Language: props.SelectedParent?.language ?? props.DefaultLanguage ?? prev.Language,
      ParentPageId: pageid > 0 ? pageid : prev.ParentPageId,
      ParentUrl: props.SelectedParent?.url ?? prev.ParentUrl
    }));

    const getData = async () => {
      try {
        await Promise.all([
          getLanguages(),
          getHeaders(),
          getFooters()
        ]);

        const initialLanguage = props.SelectedParent?.language ?? props.DefaultLanguage ?? null;

        if (initialLanguage) {
          await getPagesForLanguage(initialLanguage);
        }

      } catch (error) {
        console.log(error);
      } 
    }

    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
      getData();
    }

    if (!fvRef.current) {
      createValidator();
    }
  }, []);

  useEffect(() => {
    if (pages.length > 0 && props.SelectedParent?.url) {
      resolveParentFromUrl(props.SelectedParent.url);
    }
  }, [pages, props.SelectedParent?.url]);

  return (
    <Form id="AddPageForm" ref={formRef}>
      {(() => {
        const selectedParentOptionId = getSelectedParentOptionId();
        return (
      <div className="form-group">
        <Row>
          <Col xs={12} md={6} className="mt-2">
            <FormLabel>Site Language</FormLabel>
            <FormSelect
              title="select language"
              name="Language"
              value={pagedetail.Language ?? ""}
              onChange={handleSelectChange}>
              <option value="">Select language...</option>
              {languages?.map((lng: ILanguage) =>
                <option key={`adlng-${lng.Id}`} value={lng.ShortCode}>{lng.Language}</option>
              )}
            </FormSelect>
          </Col>
          <Col xs={12} md={6} className="mt-2">
            <FormLabel>Title</FormLabel>
            <FormControl
              type="text"
              value={pagedetail.Title ?? ""}
              onChange={handleInputChange}
              name="Title"
              placeholder="title"
              autoComplete="off"
              disabled={!langSelected} />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mt-3">
            <FormLabel>Select Parent Page</FormLabel>
            {pages && pages.length > 0 && (
              <HSelect key={`parent-page-${selectedParentOptionId || "root"}-${pages.length}`} name="ParentPageId"
                data={pages}
                options={{
                  buttonClass: "btn btn-outline-primary",
                  search: true,
                  valueProperty: 'Id',
                  value: selectedParentOptionId,
                  onChange: handleSelectChange
                }}
              />
            )}
            {!pages || pages.length <= 0 && (
              <Alert variant="info" className="text-center">
                <span className="fw-bold">No pages to select</span>
              </Alert>
            )}
          </Col>
          <Col xs={12} md={6} className="mt-3">
            <FormLabel>Url Slug</FormLabel>
            <FormControl
              type="text"
              value={pagedetail.Slug ?? ""}
              onChange={handleInputChange}
              name="Slug"
              placeholder="e.g. new-page, (leave blank for a home page)"
              autoComplete="off" 
              disabled={!langSelected} 
            />
            <div className="mt-2 text-muted">{pageUrl()}</div>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mt-3">
            <FormLabel>Alias</FormLabel>
            <FormControl
              type="text"
              value={pagedetail.Alias ?? ""}
              onChange={handleInputChange}
              name="Alias"
              placeholder="alias"
              autoComplete="off"
              disabled={!langSelected}
            />
          </Col>
          <Col xs={12} md={3} className="mt-3">
            <FormLabel htmlFor="ExcludeSiteMap">Sitemap Exclude</FormLabel>
            <Form.Check
              type="checkbox"
              id="ExcludeSiteMap"
              name="ExcludeSiteMap"
              checked={pagedetail.ExcludeSiteMap}
              onChange={handleCheckChange}
              disabled={!langSelected}
            />
          </Col>
          <Col xs={12} md={3} className="mt-3">
            <FormLabel htmlFor="IsNewsPage">News Page</FormLabel>
            <Form.Check
              type="checkbox"
              id="IsNewsPage"
              name="IsNewsPage"
              checked={pagedetail.IsNewsPage}
              onChange={handleCheckChange}
              disabled={!langSelected}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6} className="mt-3">
            <FormLabel>Header</FormLabel>
            <FormSelect
              title="select header"
              name="HeaderId"
              onChange={handleSelectChange}
              disabled={!langSelected}>
              <option>Select header...</option>
              {headers?.map((hdr: IHeader) =>
                <option key={`aphdr-${hdr.Id}`} value={hdr.Id}>{hdr.Name}</option>
              )}
            </FormSelect>
          </Col>
          <Col xs={12} md={6} className="mt-3">
            <FormLabel>Footer</FormLabel>
            <FormSelect
              title="select footer"
              name="FooterId"
              onChange={handleSelectChange}
              disabled={!langSelected}>
              <option>Select footer...</option>
              {footers?.map((ftr: IFooter) =>
                <option key={`adftr-${ftr.Id}`} value={ftr.Id}>{ftr.Title}</option>
              )}
            </FormSelect>
          </Col>
        </Row>
      </div>
        );
      })()}
    </Form>
  );
});

export default AddPageDialogue;
